import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineUserGroup,
  HiOutlineBuildingOffice2,
  HiOutlineAdjustmentsVertical,
  HiOutlineTrash,
  HiOutlineCalendar,
  HiArrowPath,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  Spinner,
} from '../../components/ui'

const ROLE_TONE = {
  admin:   'danger',
  diamond: 'info',
  gold:    'warning',
  silver:  'neutral',
}

const ROLE_OPTIONS = [
  { value: 'admin',   label: 'Admin',   desc: 'Full access to all resources' },
  { value: 'diamond', label: 'Diamond', desc: 'High access with advanced permissions' },
  { value: 'gold',    label: 'Gold',    desc: 'Moderate access with limited permissions' },
  { value: 'silver',  label: 'Silver',  desc: 'Basic read-only access' },
]

export default function ConnectionsList() {
  const { t } = useTranslation()
  const { switchWorkspace } = useAuth()
  const { getEntity } = useWorkspace()
  const navigate = useNavigate()

  const [followers, setFollowers] = useState([])
  const [following, setFollowing] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedConnection, setExpandedConnection] = useState(null)
  const [updatingRole, setUpdatingRole] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  useEffect(() => { fetchConnections() }, [])

  async function fetchConnections() {
    try {
      setLoading(true)
      const [followersResponse, followingResponse] = await Promise.all([
        apiService.get('/my-followers/'),
        apiService.get('/my-following/'),
      ])
      if (followersResponse.success) setFollowers(followersResponse.data.data || [])
      if (followingResponse.success) setFollowing(followingResponse.data.data || [])
      if (!followersResponse.success && !followingResponse.success) {
        setError(t('connections.list.couldntLoad'))
      } else {
        setError(null)
      }
    } catch {
      setError(t('connections.list.couldntLoad'))
    } finally {
      setLoading(false)
    }
  }

  async function handleRoleUpdate(connectionRequestId, accountId, newRole) {
    try {
      setUpdatingRole(accountId)
      setConfirmDialog({ isOpen: false })
      const response = await apiService.put('/connections/update-role/', {
        connection_id: connectionRequestId, new_role: newRole,
      })
      if (response.success) {
        setFollowers(prev => prev.map(conn =>
          conn.id === accountId ? { ...conn, access_level: newRole, assigned_role: newRole } : conn,
        ))
        setExpandedConnection(null)
      } else {
        alert(response.error?.message || t('connections.list.updateRole'))
      }
    } catch {
      alert(t('connections.list.updateRole'))
    } finally {
      setUpdatingRole(null)
    }
  }

  const confirmRoleUpdate = (connectionRequestId, accountId, newRole, name) => {
    setConfirmDialog({
      isOpen: true,
      title: t('connections.list.updateRoleTitle', { conn: getEntity('connection').singular }),
      message: t('connections.list.updateRoleMessage', { name, role: newRole.toUpperCase() }),
      type: 'warning',
      confirmText: t('connections.list.updateRoleConfirm'),
      onConfirm: () => handleRoleUpdate(connectionRequestId, accountId, newRole),
    })
  }

  async function handleDeleteConnection(connection) {
    try {
      setConfirmDialog({ isOpen: false })
      // /connections/manage/ expects the ConnectionRequest id, NOT the
      // counterparty's account id. /my-followers/ and /my-following/ expose
      // it as `connection_id` (the item's `id` is the account id) — sending
      // the account id made the backend load an unrelated ConnectionRequest
      // row and reject with "You can only manage your own connections."
      const response = await apiService.post('/connections/manage/', {
        connection_id: connection.connection_id || connection.id, action: 'delete',
      })
      if (response.success) {
        setFollowers(prev => prev.filter(c => c.id !== connection.id))
        setFollowing(prev => prev.filter(c => c.id !== connection.id))
      } else {
        alert(response.error?.message || t('connections.list.remove'))
      }
    } catch {
      alert(t('connections.list.remove'))
    }
  }

  const confirmDeleteConnection = (connection) => {
    setConfirmDialog({
      isOpen: true,
      title: t('connections.list.removeTitle', { conn: getEntity('connection').singular }),
      message: t('connections.list.removeMessage', {
        conn: getEntity('connection').singular.toLowerCase(),
        name: connection.name,
      }),
      type: 'danger',
      confirmText: t('connections.list.removeConfirm', { conn: getEntity('connection').singular }),
      onConfirm: () => handleDeleteConnection(connection),
    })
  }

  const toggleExpanded = (connectionId) => setExpandedConnection(
    expandedConnection === connectionId ? null : connectionId,
  )

  const formatDate = (s) => new Date(s).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  })

  const handleSwitchWorkspace = (connection) => {
    switchWorkspace(connection.id, connection.name, connection.access_level)
    navigate(`/dashboard?owner_account_id=${connection.id}`)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
        ))}
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (error) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineUserGroup}
          title={t('connections.list.couldntLoad')}
          description={error}
          action={<Button onClick={fetchConnections}>{t('common.retry')}</Button>}
        />
      </Card>
    )
  }

  const renderConnectionCard = (connection, isFollowing = false) => {
    const isExpanded = expandedConnection === connection.id
    const isUpdating = updatingRole === connection.id
    const roleKey = connection.access_level?.toLowerCase() || (isFollowing ? 'silver' : 'follower')

    return (
      <Card key={connection.id} padding="none" className="overflow-hidden">
        <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={connection.name} size="md" />
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-ink-primary truncate">{connection.name}</h4>
              <div className="flex items-center gap-2 text-xs text-ink-muted mt-1 flex-wrap">
                <Badge tone={ROLE_TONE[roleKey] || 'neutral'} size="sm">
                  {isFollowing
                    ? t('connections.list.yourRole', { role: (connection.access_level || 'silver').toUpperCase() })
                    : (connection.access_level || 'follower').toUpperCase()}
                </Badge>
                {isFollowing && connection.can_switch_to && (
                  <Badge tone="success" size="sm">{t('connections.list.canSwitch')}</Badge>
                )}
                <span className="inline-flex items-center gap-1">
                  <HiOutlineCalendar className="w-3 h-3" />
                  {t('connections.list.connectedDate', { date: formatDate(connection.following_since || connection.connected_since) })}
                </span>
              </div>
            </div>
          </div>

          {!isFollowing ? (
            <div className="flex items-center gap-1 flex-shrink-0">
              <IconButton
                icon={HiOutlineAdjustmentsVertical}
                aria-label={t('connections.list.updateRole')}
                variant="ghost"
                size="sm"
                disabled={isUpdating}
                onClick={() => toggleExpanded(connection.id)}
              />
              <IconButton
                icon={HiOutlineTrash}
                aria-label={t('connections.list.remove')}
                variant="danger"
                size="sm"
                disabled={isUpdating}
                onClick={() => confirmDeleteConnection(connection)}
              />
            </div>
          ) : (
            <Button
              size="sm"
              variant={connection.can_switch_to ? 'primary' : 'secondary'}
              disabled={!connection.can_switch_to}
              onClick={() => handleSwitchWorkspace(connection)}
            >
              {connection.can_switch_to ? t('connections.list.switchWorkspace') : t('connections.list.cannotSwitch')}
            </Button>
          )}
        </div>

        {isExpanded && !isFollowing && (
          <div className="px-4 pb-4 border-t border-border-subtle">
            <div className="pt-4">
              <h5 className="text-sm font-semibold text-ink-primary mb-3">{t('connections.list.updateRole')}</h5>
              <div className="space-y-2">
                {ROLE_OPTIONS.map((opt) => {
                  const isCurrent = connection.access_level?.toLowerCase() === opt.value
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isCurrent ? 'border-brand bg-brand-soft' : 'border-border hover:border-border-strong'
                      } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`role-${connection.id}`}
                        value={opt.value}
                        checked={isCurrent}
                        onChange={() => {
                          if (!isUpdating && connection.connection_id) {
                            confirmRoleUpdate(connection.connection_id, connection.id, opt.value, connection.name)
                          }
                        }}
                        disabled={isUpdating}
                        className="h-4 w-4 text-brand border-border-strong focus:ring-brand/30 focus:ring-2"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-ink-primary">{opt.label}</div>
                        <div className="text-xs text-ink-muted">{opt.desc}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* My followers (employees connected to me) */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <HiOutlineUserGroup className="w-5 h-5 text-ink-muted" />
            <h3 className="text-base font-semibold text-ink-primary">{t('connections.list.myFollowers')}</h3>
            <span className="text-xs text-ink-muted">({getEntity('employee').plural})</span>
            {followers.length > 0 && <Badge tone="brand" size="sm">{followers.length}</Badge>}
          </div>
          <Button variant="link" size="sm" leadingIcon={HiArrowPath} onClick={fetchConnections}>
            {t('common.refresh')}
          </Button>
        </div>
        {followers.length === 0 ? (
          <Card padding="md">
            <EmptyState
              icon={HiOutlineUserGroup}
              title={t('connections.list.noFollowers', { entity: getEntity('employee').plural.toLowerCase() })}
              description={t('connections.list.noFollowersHelp', {
                entity: getEntity('employee').plural.toLowerCase(),
                conn: getEntity('connection').singular.toLowerCase(),
              })}
              compact
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {followers.map((c) => renderConnectionCard(c, false))}
          </div>
        )}
      </section>

      {/* My following (owners I'm connected to) */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <HiOutlineBuildingOffice2 className="w-5 h-5 text-ink-muted" />
            <h3 className="text-base font-semibold text-ink-primary">{t('connections.list.myFollowing')}</h3>
            <span className="text-xs text-ink-muted">({getEntity('owner').plural})</span>
            {following.length > 0 && <Badge tone="brand" size="sm">{following.length}</Badge>}
          </div>
          <Button variant="link" size="sm" leadingIcon={HiArrowPath} onClick={fetchConnections}>
            {t('common.refresh')}
          </Button>
        </div>
        {following.length === 0 ? (
          <Card padding="md">
            <EmptyState
              icon={HiOutlineBuildingOffice2}
              title={t('connections.list.noFollowing', { entity: getEntity('owner').plural.toLowerCase() })}
              description={t('connections.list.noFollowingHelp', {
                conn: getEntity('connection').singular.toLowerCase(),
                entity: getEntity('owner').plural.toLowerCase(),
                workspace: getEntity('workspace').plural.toLowerCase(),
              })}
              compact
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {following.map((c) => renderConnectionCard(c, true))}
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}
